import _ from "lodash";
import {
  createResource,
  deleteResource,
  createResourcePath,
  determinePaginationParams,
  determineSelector,
  getResource,
  readUrl,
  updateResource,
  updateResourcePath,
} from "./api";

// query/exclude/select mirror experts.getAll's shape (a picker's search + exclusion-set + column
// projection needs). Pagination keeps its pre-existing 12-row default and honors both the legacy
// `per_page` spelling and the camelCase `perPage` other callers use (previously `per_page` was
// silently ignored).
const getAll = async (options = {}) => {
  const { exclude, query, page = 1, perPage, per_page: perPageLegacy, ...otherOptions } = options;
  const paginationParams = determinePaginationParams({
    page,
    per_page: perPage ?? perPageLegacy ?? 12,
    returnType: "object",
  });

  let selector = { order: "name", ...otherOptions, ...paginationParams };
  if (!_.isEmpty(query)) selector = { ...selector, name: `ilike.*${query}*` };
  if (!_.isEmpty(exclude)) selector = { ...selector, id: `not.in.(${exclude.join(",")})` };

  const url = readUrl("companies", { selector: new URLSearchParams(selector).toString() });
  return getResource(url);
};

const get = async (id) => {
  const selector = determineSelector(id, { keyId: "id", keySlug: "slug" });
  const url = readUrl("companies", { selector });
  return getResource(url);
};

const updateById = async (id, attributes) => {
  const url = updateResourcePath("companies", id);
  return updateResource(url, { data: { attributes } });
};

const createBannerImageById = async (companyId, attributes) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/banner_images`;
  return createResource(url, { data: { attributes } });
};

// banner_images is a PLURAL route (`resources :banner_images`, config/routes.rb) even though a
// company has at most one, so update/destroy need a trailing id segment to match it.
// Id::Api::ImagesController resolves the banner by owner+role and ignores that segment's value
// (see ImagesController#set_image) -- the companies API view exposes only banner_image_url, never
// the image row's id, so "current" is a placeholder, not a real identifier.
const updateBannerImageById = async (companyId, attributes) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/banner_images/current`;
  return updateResource(url, { data: { attributes } });
};

const deleteBannerImageById = async (companyId) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/banner_images/current`;
  return deleteResource(url);
};

const createProfileImageById = async (companyId, attributes) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/profile_image`;
  return createResource(url, { data: { attributes } });
};

const updateProfileImageById = async (companyId, attributes) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/profile_image`;
  return updateResource(url, { data: { attributes } });
};

const deleteProfileImageById = async (companyId, attributes) => {
  let url = updateResourcePath("companies", companyId);
  url = `${url}/profile_image`;
  return deleteResource(url, { data: { attributes } });
};

const create = async (attributes) => {
  const url = createResourcePath("companies");
  return createResource(url, { data: { attributes } });
};

export default {
  create,
  createBannerImageById,
  createProfileImageById,
  deleteBannerImageById,
  deleteProfileImageById,
  get,
  getAll,
  updateBannerImageById,
  updateById,
  updateProfileImageById,
};
